import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api } from "../lib/api";
import { useSettings } from "../lib/settings";
import { toast } from "sonner";
import { CreditCard, Smartphone, DollarSign, CheckCircle2 } from "lucide-react";

function buildMethods(settings) {
  return [
    { id: "stripe", label: "Pay with card", icon: CreditCard, note: "Apple Pay · Google Pay · Visa · Mastercard · Amex" },
    { id: "paypal", label: "PayPal", icon: CreditCard, note: settings.paypal_handle || "Add handle in Site Settings" },
    { id: "zelle", label: "Zelle", icon: DollarSign, note: settings.zelle_handle || "Add handle in Site Settings" },
    { id: "cashapp", label: "Cash App", icon: Smartphone, note: settings.cashapp_handle || "Add handle in Site Settings" },
    { id: "venmo", label: "Venmo", icon: Smartphone, note: settings.venmo_handle || "Add handle in Site Settings" },
  ];
}

const EMPTY_ADDRESS = { line1: "", line2: "", city: "", state: "", postal_code: "", country: "US" };

// Defined at MODULE SCOPE — if defined inside PaymentModal these get
// re-created on every keystroke, which causes React to unmount/remount the
// underlying <input> and the focus is lost after each character.
function Field({ value, onChange, placeholder, type = "text", required = false, testid, full }) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      type={type}
      required={required}
      data-testid={testid}
      className={`${full ? "sm:col-span-2" : ""} px-4 py-3 rounded-xl bg-[#FDFBF7] border border-[#EAE4D3] focus:outline-none focus:ring-2 focus:ring-[#D4AF37] text-sm`}
    />
  );
}

function AddressFieldset({ value, onChange, testidPrefix }) {
  const set = (key, v) => onChange({ ...value, [key]: v });
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      <Field value={value.line1} onChange={(v) => set("line1", v)} placeholder="Street address" required testid={`${testidPrefix}-line1`} full />
      <Field value={value.line2} onChange={(v) => set("line2", v)} placeholder="Apt / Suite (optional)" testid={`${testidPrefix}-line2`} full />
      <Field value={value.city} onChange={(v) => set("city", v)} placeholder="City" required testid={`${testidPrefix}-city`} />
      <Field value={value.state} onChange={(v) => set("state", v)} placeholder="State (2-letter, e.g. CA)" required testid={`${testidPrefix}-state`} />
      <Field value={value.postal_code} onChange={(v) => set("postal_code", v)} placeholder="ZIP / Postal code" required testid={`${testidPrefix}-zip`} />
      <Field value={value.country} onChange={(v) => set("country", v.toUpperCase())} placeholder="Country (e.g. US)" required testid={`${testidPrefix}-country`} />
    </div>
  );
}

export default function PaymentModal({ open, onOpenChange, product, variant, engraving }) {
  const { settings } = useSettings();
  const METHODS = buildMethods(settings);

  // Step machine: physical -> "info" -> "method" -> "pay" -> "done"
  //                digital  -> "method" -> "pay" -> "done"
  const isPhysical = !product?.is_digital;
  const [step, setStep] = useState(isPhysical ? "info" : "method");
  const [method, setMethod] = useState(null);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [shipping, setShipping] = useState(EMPTY_ADDRESS);
  const [billing, setBilling] = useState(EMPTY_ADDRESS);
  const [sameAsShipping, setSameAsShipping] = useState(true);
  const [reference, setReference] = useState("");
  const [order, setOrder] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const unitPrice = variant?.price ?? product?.price ?? 0;

  // Auto-fill from saved customer profile when modal opens (no login needed)
  useEffect(() => {
    if (!open) return;
    let stored = "";
    try { stored = localStorage.getItem("ttfm_customer_email") || localStorage.getItem("ttfm_wishlist_email") || ""; } catch { /* ignore */ }
    if (!stored) return;
    api.get(`/profile/by-email?email=${encodeURIComponent(stored)}`)
      .then((r) => {
        const p = r.data || {};
        setEmail((cur) => cur || p.email || "");
        setName((cur) => cur || p.name || "");
        setPhone((cur) => cur || p.phone || "");
        if (p.default_address) {
          setShipping((cur) => (cur.line1 ? cur : { ...EMPTY_ADDRESS, ...p.default_address }));
        }
      })
      .catch(() => { /* ignore */ });
  }, [open]);

  function reset() {
    setStep(isPhysical ? "info" : "method");
    setMethod(null); setName(""); setEmail(""); setPhone("");
    setShipping(EMPTY_ADDRESS); setBilling(EMPTY_ADDRESS); setSameAsShipping(true);
    setReference(""); setOrder(null);
  }

  function close(v) {
    if (!v) reset();
    onOpenChange(v);
  }

  function submitInfo(e) {
    e.preventDefault();
    setStep("method");
  }

  function pickMethod(m) {
    setMethod(m);
    setStep("pay");
  }

  async function startStripeCheckout() {
    setSubmitting(true);
    try {
      const { data } = await api.post("/checkout/stripe/session", {
        product_id: product.id,
        customer_name: name,
        customer_email: email,
        customer_phone: phone || null,
        origin_url: window.location.origin,
        variant_id: variant?.id || null,
        variant_name: variant?.name || null,
        engraving: engraving || null,
        shipping_address: isPhysical ? shipping : null,
        billing_address: isPhysical ? (sameAsShipping ? shipping : billing) : null,
      });
      try { localStorage.setItem("ttfm_customer_email", (email || "").toLowerCase()); } catch { /* ignore */ }
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start card checkout. Try another method.");
      setSubmitting(false);
    }
  }

  async function submitOrder(e) {
    e.preventDefault();
    if (method?.id === "stripe") {
      await startStripeCheckout();
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders", {
        product_id: product.id,
        customer_name: name,
        customer_email: email,
        customer_phone: phone || null,
        payment_method: method.id,
        payment_reference: reference,
        variant_id: variant?.id || null,
        variant_name: variant?.name || null,
        engraving: engraving || null,
        shipping_address: isPhysical ? shipping : null,
        billing_address: isPhysical ? (sameAsShipping ? shipping : billing) : null,
      });
      // Remember customer for next visit so /account autoloads
      try { localStorage.setItem("ttfm_customer_email", (email || "").toLowerCase()); } catch { /* ignore */ }
      setOrder(data);
      setStep("done");
      toast.success("Order placed! We'll release your shipment once payment is confirmed.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  // Field + AddressFieldset are defined at module scope (top of this file).
  // Defining them inside this component caused React to remount the <input>
  // on every keystroke → focus lost after each character. Do NOT inline them here.

  const title =
    step === "done" ? "Order received" : `Buy ${product?.title || ""}`;
  const description =
    step === "info" ? "We'll use this address to ship your order."
    : step === "method" ? (variant?.name ? `Selected: ${variant.name}. Choose your payment method.` : "Choose your preferred payment method.")
    : step === "pay" ? `Send $${unitPrice.toFixed(2)} via ${method?.label} and submit your details.`
    : "We'll email you a confirmation as soon as payment is verified.";

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="payment-modal" className="max-w-lg bg-[#FDFBF7] border-[#EAE4D3] rounded-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{title}</DialogTitle>
          <DialogDescription className="text-[#5C5547]">{description}</DialogDescription>
        </DialogHeader>

        {step === "info" && (
          <form onSubmit={submitInfo} className="space-y-4 mt-2" data-testid="info-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Field value={name} onChange={setName} placeholder="Full name" required testid="info-name" full />
              <Field value={email} onChange={setEmail} placeholder="Email" type="email" required testid="info-email" />
              <Field value={phone} onChange={setPhone} placeholder="Phone (for shipping updates)" testid="info-phone" />
            </div>

            <div>
              <div className="uppercase text-[10px] tracking-[0.2em] text-[#5C5547] mb-2">Ship to</div>
              <AddressFieldset value={shipping} onChange={setShipping} testidPrefix="ship" />
            </div>

            <label className="flex items-center gap-2 text-sm" data-testid="same-as-shipping">
              <input type="checkbox" checked={sameAsShipping} onChange={(e) => setSameAsShipping(e.target.checked)} />
              Billing address same as shipping
            </label>

            {!sameAsShipping && (
              <div>
                <div className="uppercase text-[10px] tracking-[0.2em] text-[#5C5547] mb-2">Bill to</div>
                <AddressFieldset value={billing} onChange={setBilling} testidPrefix="bill" />
              </div>
            )}

            <button type="submit" data-testid="info-continue"
              className="w-full py-3 rounded-full bg-[#2C2924] text-[#FDFBF7] hover:bg-[#1A1815] text-sm">
              Continue to payment →
            </button>
          </form>
        )}

        {step === "method" && (
          <div className="grid gap-3 mt-2">
            {METHODS.map((m) => {
              const Icon = m.icon;
              return (
                <button key={m.id} data-testid={`pay-method-${m.id}`}
                  onClick={() => pickMethod(m)}
                  className="group flex items-center justify-between p-4 rounded-2xl border border-[#EAE4D3] hover:border-[#D4AF37] hover:bg-[#F5F2EA] transition">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#F5F2EA] group-hover:bg-[#FDFBF7] flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[#2C2924]" />
                    </div>
                    <div className="text-left">
                      <div className="text-sm font-medium">{m.label}</div>
                      <div className="text-xs text-[#5C5547]">{m.note}</div>
                    </div>
                  </div>
                </button>
              );
            })}
            {isPhysical && (
              <button onClick={() => setStep("info")} data-testid="back-to-info"
                className="text-xs text-[#5C5547] hover:text-[var(--accent)] mt-2">← Edit shipping info</button>
            )}
          </div>
        )}

        {step === "pay" && method && (
          <form onSubmit={submitOrder} className="space-y-4 mt-2" data-testid="payment-form">
            <div className="rounded-2xl bg-[#F5F2EA] p-4 text-sm">
              <div className="uppercase text-[10px] tracking-[0.2em] text-[#5C5547]">{method.label}</div>
              <div className="font-serif text-lg mt-1">{method.note}</div>
              {engraving && (
                <div className="mt-2 text-xs text-[#5C5547]" data-testid="engraving-confirmation">
                  Engraving: <span className="font-serif italic text-[#2C2924]">"{engraving}"</span>
                </div>
              )}
              {method.id === "stripe" ? (
                <div className="mt-2 text-[#5C5547]">Total: <span className="font-semibold text-[#2C2924]">${unitPrice.toFixed(2)}</span>. You'll be redirected to a secure Stripe page.</div>
              ) : (
                <div className="mt-2 text-[#5C5547]">Send <span className="font-semibold text-[#2C2924]">${unitPrice.toFixed(2)}</span>, then fill the form below.</div>
              )}
            </div>

            {/* For DIGITAL products, contact info is collected here */}
            {!isPhysical && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Field value={name} onChange={setName} placeholder="Full name" required testid="order-name-input" full />
                <Field value={email} onChange={setEmail} placeholder="Email for delivery" type="email" required testid="order-email-input" full />
              </div>
            )}

            {/* For PHYSICAL products, contact + address already collected; show summary */}
            {isPhysical && (
              <div className="rounded-2xl border border-[#EAE4D3] p-3 text-xs text-[#5C5547]" data-testid="ship-summary">
                <div className="font-semibold text-[#2C2924]">{name} · {email}{phone ? ` · ${phone}` : ""}</div>
                <div className="mt-1">{shipping.line1}{shipping.line2 ? `, ${shipping.line2}` : ""}, {shipping.city}, {shipping.state} {shipping.postal_code}, {shipping.country}</div>
              </div>
            )}

            {method.id !== "stripe" && (
              <Field value={reference} onChange={setReference} placeholder="Payment reference / username (optional)" testid="order-reference-input" full />
            )}

            <div className="flex gap-2">
              <button type="button" onClick={() => setStep("method")} data-testid="payment-back"
                className="flex-1 py-3 rounded-full bg-[#F5F2EA] text-[#2C2924] hover:bg-[#EAE4D3] text-sm">Back</button>
              <button type="submit" disabled={submitting} data-testid="payment-submit"
                className="flex-1 py-3 rounded-full bg-[#2C2924] text-[#FDFBF7] hover:bg-[#1A1815] text-sm disabled:opacity-60">
                {submitting ? (method.id === "stripe" ? "Redirecting…" : "Submitting…") : (method.id === "stripe" ? `Pay $${unitPrice.toFixed(2)} →` : "Submit Order")}
              </button>
            </div>
          </form>
        )}

        {step === "done" && order && (
          <div className="text-center py-6" data-testid="order-success">
            <div className="w-14 h-14 mx-auto rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7 text-[#D4AF37]" />
            </div>
            <div className="mt-4 font-serif text-2xl">Thank you, {order.customer_name.split(" ")[0]}.</div>
            <p className="mt-2 text-sm text-[#5C5547]">
              Your order ID is <span className="font-mono text-[#2C2924]">{order.id.slice(0, 8)}</span>.
            </p>
            <p className="mt-1 text-sm text-[#5C5547]">
              Once we verify your {method.label} payment, we'll {isPhysical ? "ship your order" : "email a download link"} to <strong>{order.customer_email}</strong>.
            </p>
            <button onClick={() => close(false)} data-testid="order-success-close"
              className="mt-6 px-6 py-2.5 rounded-full bg-[#2C2924] text-[#FDFBF7] text-sm">Close</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
